/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

(function () {
  var acknowledgementSelector = '[data-shipping-acknowledgement-input]';
  var checkoutSelector = '[data-shipping-acknowledgement-checkout]';
  var noteSelector = '[data-cart-order-note]';
  var accepted = null;
  var savedAccepted = null;
  var note = null;
  var savedNote = null;
  var updateRequest = null;
  var saveTimer = null;
  var submitting = false;
  var resuming = false;

  function noteStatus(message) {
    document.querySelectorAll('[data-cart-order-note-status]').forEach(function (status) {
      status.textContent = message;
    });
  }

  function setAcknowledgementState(value) {
    document.querySelectorAll(acknowledgementSelector).forEach(function (input) { input.checked = value; });
    document.querySelectorAll(checkoutSelector).forEach(function (button) { button.disabled = !value || submitting; });
    if (value) document.querySelectorAll('[data-shipping-acknowledgement-error]').forEach(function (error) { error.hidden = true; });
  }

  function readNote() {
    var input = document.querySelector(noteSelector);
    if (!input) return;
    if (savedNote === null) savedNote = input.defaultValue;
    note = input.value;
  }

  function dirty() {
    return (note !== null && note !== savedNote) || (accepted !== null && accepted !== savedAccepted);
  }

  // All note/notice writes share one queue. A slower old response cannot undo
  // newer typing or a checkbox change, and checkout waits for the latest values.
  function flushCartDetails() {
    clearTimeout(saveTimer);
    readNote();
    if (updateRequest) return updateRequest;
    if (!dirty()) return Promise.resolve();
    noteStatus('Saving instructions…');
    var task = (async function () {
      while (dirty()) {
        var body = {};
        var nextNote = note;
        var nextAccepted = accepted;
        if (note !== null && note !== savedNote) body.note = nextNote;
        if (accepted !== null && accepted !== savedAccepted) {
          body.attributes = { 'Shipping acknowledgement': nextAccepted ? 'Accepted' : '' };
        }
        var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : null;
        var url = root ? root + 'cart/update.js' : window.routes.cartUrl + '/update.js';
        var response = await (window.erhCartRequest || window.fetch.bind(window))(url, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
        if (!response.ok) throw new Error('Cart details could not be saved. Please try checkout again.');
        var cart = await response.json();
        if ('note' in body) {
          if ((cart.note || '') !== nextNote) throw new Error('Order instructions could not be saved. Please try checkout again.');
          savedNote = nextNote;
        }
        if (body.attributes) {
          if ((cart.attributes && cart.attributes['Shipping acknowledgement'] === 'Accepted') !== nextAccepted) {
            throw new Error('The notice acknowledgement could not be saved. Please try checkout again.');
          }
          savedAccepted = nextAccepted;
        }
      }
    })();
    updateRequest = task.then(function () {
      updateRequest = null;
      noteStatus('Instructions saved.');
      document.querySelectorAll('[data-shipping-acknowledgement-error]').forEach(function (error) { error.hidden = true; });
    }, function (error) {
      updateRequest = null;
      noteStatus(error.message);
      document.querySelectorAll('[data-shipping-acknowledgement-error]').forEach(function (element) {
        element.textContent = error.message;
        element.hidden = false;
      });
      throw error;
    });
    return updateRequest;
  }

  function saveInBackground() { flushCartDetails().catch(function () {}); }
  // Availability verification calls this before reading/reconciling the cart.
  window.erhPrepareCartCheckout = flushCartDetails;

  document.addEventListener('input', function (event) {
    if (!event.target.matches(noteSelector)) return;
    readNote();
    noteStatus('Saving instructions…');
    clearTimeout(saveTimer);
    saveTimer = setTimeout(saveInBackground, 400);
  });

  document.addEventListener('change', function (event) {
    if (event.target.matches(noteSelector)) { saveInBackground(); return; }
    if (!event.target.matches(acknowledgementSelector)) return;
    if (savedAccepted === null) savedAccepted = event.target.defaultChecked;
    accepted = event.target.checked;
    setAcknowledgementState(accepted);
    saveInBackground();
  });

  document.addEventListener('cart:rerendered', function () {
    // Quantity updates replace the form. Preserve the latest local edits even
    // when the section HTML was requested before their save finished.
    if (note !== null) document.querySelectorAll(noteSelector).forEach(function (input) { input.value = note; });
    if (accepted !== null) setAcknowledgementState(accepted);
  });

  document.addEventListener('submit', function (event) {
    var button = event.submitter;
    if (!button || !button.matches(checkoutSelector)) return;
    var form = event.target;
    var acknowledgement = form.querySelector(acknowledgementSelector);
    if (!acknowledgement || !acknowledgement.checked) {
      event.preventDefault();
      var error = form.querySelector('[data-shipping-acknowledgement-error]');
      if (error) {
        error.textContent = 'Please acknowledge the shipping information before proceeding to checkout.';
        error.hidden = false;
      }
      return;
    }
    if (resuming) return;
    readNote();
    if (!updateRequest && !dirty()) return;
    event.preventDefault();
    if (submitting) return;
    submitting = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    flushCartDetails().then(function () {
      // Resolve the current form/button: a cart quantity refresh may have
      // replaced the originals while their save request was in flight.
      var current = button.isConnected ? button : document.querySelector(checkoutSelector);
      var currentForm = current && current.form;
      var currentAck = currentForm && currentForm.querySelector(acknowledgementSelector);
      if (!currentAck || !currentAck.checked) return;
      current.disabled = false;
      resuming = true;
      try { currentForm.requestSubmit(current); } finally { resuming = false; }
    }).catch(function () {
      // The save error is shown above. Keep the typed note so checkout can retry.
    }).finally(function () {
      submitting = false;
      document.querySelectorAll(checkoutSelector).forEach(function (current) {
        current.removeAttribute('aria-busy');
        var ack = current.form && current.form.querySelector(acknowledgementSelector);
        current.disabled = !ack || !ack.checked;
      });
    });
  });
})();

(function () {
  var rootSelector = '[data-cart-wishlist]';
  var endpoint = '/apps/deals/wishlists';

  function rootFor(element) {
    return element && element.closest(rootSelector);
  }

  function setMessage(message, isError) {
    var target = document.querySelector('[data-wishlist-transfer-message]');
    if (!target) return;
    target.setAttribute('role', isError ? 'alert' : 'status');
    target.textContent = message || '';
    target.hidden = !message;
    target.classList.toggle('cart-wishlist-transfer-message--error', Boolean(isError));
    if (message) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function setDialogMessage(root, message, isError) {
    var target = root && root.querySelector('[data-wishlist-dialog-message]');
    if (!target) return;
    target.textContent = message || '';
    target.hidden = !message;
    target.classList.toggle('cart-wishlist__message--error', Boolean(isError));
  }

  async function responseJson(response) {
    var payload = await response.json().catch(function () { return {}; });
    if (!response.ok) throw new Error(payload.message || 'Unable to update the wishlist.');
    return payload;
  }

  async function cartItems() {
    var rootUrl = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';
    var cart = await responseJson(await fetch(rootUrl + 'cart.js', {
      headers: { Accept: 'application/json' }
    }));
    return (cart.items || []).map(function (item) {
      return { variantId: item.variant_id, quantity: item.quantity };
    });
  }

  async function saveCart(intent, publicId) {
    var items = await cartItems();
    return responseJson(await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ intent: intent, publicId: publicId, items: items })
    }));
  }

  function setBusy(button, busy, busyText) {
    if (!button) return;
    if (busy) {
      button.dataset.originalText = button.textContent;
      button.textContent = busyText;
      button.disabled = true;
    } else {
      button.textContent = button.dataset.originalText || button.textContent;
      button.disabled = false;
    }
  }

  document.addEventListener('click', async function (event) {
    var createButton = event.target.closest('[data-wishlist-create]');
    if (createButton) {
      setBusy(createButton, true, 'Creating…');
      setMessage('', false);
      try {
        var createResult = await saveCart('create');
        setMessage(createResult.wishlist.title + ' created.', false);
      } catch (error) {
        setMessage(error.message, true);
      } finally {
        setBusy(createButton, false);
      }
      return;
    }

    var openButton = event.target.closest('[data-wishlist-open]');
    if (openButton) {
      var openRoot = rootFor(openButton);
      var dialog = openRoot.querySelector('[data-wishlist-dialog]');
      var select = openRoot.querySelector('[data-wishlist-select]');
      setBusy(openButton, true, 'Loading…');
      setMessage('', false);
      setDialogMessage(openRoot, '', false);
      try {
        var listResult = await responseJson(await fetch(endpoint, {
          headers: { Accept: 'application/json' }
        }));
        select.textContent = '';
        (listResult.wishlists || []).forEach(function (wishlist) {
          var option = document.createElement('option');
          option.value = wishlist.publicId;
          option.textContent = wishlist.title + ' (' + wishlist.itemCount + ' items)';
          select.appendChild(option);
        });
        if (!select.options.length) {
          throw new Error('You do not have an existing wishlist yet.');
        }
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', 'open');
      } catch (error) {
        setMessage(error.message, true);
      } finally {
        setBusy(openButton, false);
      }
      return;
    }

    var addButton = event.target.closest('[data-wishlist-add]');
    if (addButton) {
      var addRoot = rootFor(addButton);
      var addDialog = addRoot.querySelector('[data-wishlist-dialog]');
      var selected = addRoot.querySelector('[data-wishlist-select]').value;
      setBusy(addButton, true, 'Adding…');
      setDialogMessage(addRoot, '', false);
      try {
        var addResult = await saveCart('add', selected);
        if (typeof addDialog.close === 'function') addDialog.close();
        else addDialog.removeAttribute('open');
        setMessage('Items added to ' + addResult.wishlist.title + '.', false);
      } catch (error) {
        setDialogMessage(addRoot, error.message, true);
      } finally {
        setBusy(addButton, false);
      }
    }
  });
})();

(function () {
  var transferParameter = 'erh_wishlist_transfer';
  var variantParameter = 'erh_wishlist_variant';
  var completionKey = 'erh-wishlist-cart-message';

  function showTransferMessage(message, isError) {
    var target = document.querySelector('[data-wishlist-transfer-message]');
    if (!target) {
      target = document.createElement('p');
      target.className = 'cart-wishlist-transfer-message rounded bg-white mt-6 shadow fs-5 fw-bold px-20 py-4';
      var container = document.querySelector('main') || document.body;
      container.insertBefore(target, container.firstChild);
    }
    target.setAttribute('role', isError ? 'alert' : 'status');
    target.textContent = message || '';
    target.hidden = !message;
    target.classList.toggle('cart-wishlist-transfer-message--error', Boolean(isError));
  }

  async function jsonResponse(response, fallbackMessage) {
    var payload = await response.json().catch(function () { return {}; });
    if (!response.ok) {
      throw new Error(payload.message || payload.description || fallbackMessage);
    }
    return payload;
  }

  async function transferWishlistToCart(publicId, variantId) {
    showTransferMessage('Adding wishlist items to your cart…', false);
    var query = new URLSearchParams({ id: publicId });
    if (variantId) query.set('variant', variantId);

    var transfer = await jsonResponse(await fetch(
      '/apps/deals/wishlists/transfer?' + query.toString(),
      { headers: { Accept: 'application/json' } }
    ), 'Unable to load wishlist items.');

    var rootUrl = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';
    await jsonResponse(await (window.erhCartRequest || fetch)(rootUrl + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: transfer.items || [] })
    }), 'The wishlist items could not be added to your cart.');

    window.sessionStorage.setItem(completionKey, 'Wishlist items added to your cart.');
    window.location.reload();
  }

  var completedMessage = window.sessionStorage.getItem(completionKey);
  if (completedMessage) {
    window.sessionStorage.removeItem(completionKey);
    showTransferMessage(completedMessage, false);
  }

  var url = new URL(window.location.href);
  var publicId = url.searchParams.get(transferParameter);
  if (!publicId) return;
  var variantId = url.searchParams.get(variantParameter) || '';

  // Remove the transfer parameters before the request so reloads and browser
  // history can never add the same wishlist items twice.
  url.searchParams.delete(transferParameter);
  url.searchParams.delete(variantParameter);
  window.history.replaceState({}, document.title, url.toString());

  transferWishlistToCart(publicId, variantId).catch(function (error) {
    showTransferMessage(error.message || 'Unable to add wishlist items to your cart.', true);
  });
})();
